import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationExtras, Router, UrlTree } from '@angular/router';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { BreadcrumbService } from './breadcrumb.service';
import { menu, NavItem } from './menu';

export interface Breadcrumb {
  name: string | undefined;
  url: string | undefined;
  queryParams?: any;
  pauseDisplay?: boolean;
}

@Component({
    selector: 'app-breadcrumb',
    templateUrl: './breadcrumb.component.html',
    styleUrls: ['./breadcrumb.component.scss'],
    standalone: false
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  breadcrumbs: Breadcrumb[] = [];
  currentUrl: string = '';
  private sub: any;
  private params?: any;
  queryParams: any;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private breadcrumbService: BreadcrumbService
  ) { }

  ngOnInit() {

    this.sub = this.activatedRoute.firstChild.params.subscribe(params => {
      this.params = params;
      this.setDefaultBreadcrumb();
      this.listenForRouteChange();
      this.listenForBreadcrumbUpdate();
      this.listenForBreadcrumbPop();
      this.searchQueryParameters();
    });
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  searchQueryParameters() {
    const tree: UrlTree = this.router.parseUrl(this.router.url);
    let route = this.router.url;
    if (tree.queryParams) {
      Object.keys(tree.queryParams).forEach((key) => {
        const r = '?' + key + '=' + tree.queryParams[key];
        route = route.replace(r, '');
      });
    }
  }

  isLanding(): boolean {
    return this.router.url === '/landing' || this.router.url === '/' || this.router.url.startsWith('/user/signin');
  }

  private setDefaultBreadcrumb() {

    this.setCurrentUrl();

    if (this.currentUrl) {
      let routes = this.findRoute();
      if (!routes || routes.length === 0) {
        routes = [menu[0]];
      }

      const navItem: NavItem[] = routes;

      if (navItem.length > 0) {
        for (const ni of navItem) {
          let route = ni.route;

          if (this.params) {
            Object.keys(this.params).forEach((key) => {
              route = route.replace(':' + key, this.params[key]);
            });
          }

          const breadcrumb: Breadcrumb = { name: ni.displayName, url: route };

          this.breadcrumbs.push(breadcrumb);
        }
      }
    }
  }

  private listenForBreadcrumbUpdate() {
    this.breadcrumbService.onUpdate().subscribe((str: string) => {
      this.handleBreadcrumbUpdate(str);
    });
  }

  private listenForBreadcrumbPop() {
    this.breadcrumbService.onPop().subscribe((extras: NavigationExtras) => {
      this.handleBreadcrumbPop(extras);
    });
  }

  private handleBreadcrumbPop(extras: NavigationExtras) {
    this.routeTo(this.breadcrumbs.length - 2, extras);
  }

  private handleBreadcrumbUpdate(str: string) {
    if (this.breadcrumbs.length === 0) {
      this.setDefaultBreadcrumb();
      const dummyBreadcrumb: Breadcrumb = { name: '', url: '' };
      this.breadcrumbs.push(dummyBreadcrumb);
    }
    const lastBreadcrumb: Breadcrumb = this.breadcrumbs[this.breadcrumbs.length - 1];

    lastBreadcrumb.pauseDisplay = false;
    lastBreadcrumb.name = str;
  }

  private listenForRouteChange() {
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        distinctUntilChanged()
      )
      .subscribe((route: ActivatedRoute) => this.handleCurrentRoute(route));
  }

  private handleCurrentRoute(route: ActivatedRoute) {
    this.setCurrentUrl();

    const navItem: NavItem[] = this.findRoute(menu);

    if (navItem.length > 0) {
      this.handleTopLevelBreadcrumb(navItem);
    } else {
      this.addBreadcrumb(route);
    }
  }

  private addBreadcrumb(route: ActivatedRoute) {
    if (this.breadcrumbs.length > 6) {
      this.breadcrumbs.splice(1, 1);
    }

    let breadcrumb: Breadcrumb | undefined | null = null;

    route.data.subscribe((data: any) => {
      breadcrumb = { name: data.breadcrumb, url: this.currentUrl, pauseDisplay: data.pauseDisplay };
      if (breadcrumb.url != this.breadcrumbs[this.breadcrumbs.length - 1].url) {
        route.queryParams.subscribe((queryParams: any) => {
          if (queryParams && breadcrumb) {
            breadcrumb.queryParams = queryParams;
          }
        });
        this.breadcrumbs.push(breadcrumb);
      }
    });
  }

  private handleTopLevelBreadcrumb(navItem: NavItem[]) {
    this.breadcrumbs = [];

    for (const ni of navItem) {
      const breadcrumb: Breadcrumb = { name: ni?.displayName, url: ni?.route };
      this.breadcrumbs.push(breadcrumb);
    }
  }

  private findRoute(navItems?: NavItem[]): NavItem[] {

    if (!navItems) navItems = menu;
    if (this.currentUrl) {
      let route = this.currentUrl;

      if (this.params) {
        Object.keys(this.params).forEach((key) => {
          route = route.replace(this.params[key], ':' + key);
        });
      }

      for (const item of navItems) {
        if (route == item.route) {
          return [item];
        } else if (item.children) {
          const childRoutes = this.findRoute(item.children);

          if (childRoutes.length > 0) {
            return [item].concat(childRoutes);
          }
        }
      }
    }

    return [];
  }

  routeTo(index: number, extras?: NavigationExtras) {
    if (index < this.breadcrumbs.length - 1) {
      // if the user clicked on a link in the breadcrumbs list,
      // delete every breadcrumb after that point
      this.breadcrumbs.splice(index + 1);
    }

    const breadcrumb: Breadcrumb = this.breadcrumbs[index];

    const route = breadcrumb.url;

    if (extras) {
      this.router.navigate([route], extras);
    } else {
      this.router.navigate([route], { queryParams: breadcrumb.queryParams });
    }
  }

  private setCurrentUrl() {
    const url: string = this.router.url;
    const tree: UrlTree = this.router.parseUrl(this.router.url);
    let route = this.router.url;
    if (tree.queryParams) {
      Object.keys(tree.queryParams).forEach((key) => {
        const r = '?' + key + '=' + tree.queryParams[key];
        route = route.replace(r, '');
      });
      this.currentUrl = route;
    } else {
      this.currentUrl = url;
    }
  }
}
